import { useCallback } from 'react'
import { queryKeys } from '@/hooks/query-keys'
import { featureFlagsApi } from '@/lib/api/feature-flags.api'
import { useOrganizationStore } from '@/store/organization.store'
import { useQuery } from '@tanstack/react-query'

import type { FeatureFlagKeyType } from '@scrambl/shared'
import { FeatureFlagKey } from '@scrambl/shared'

type FeatureFlagCheckMap = Partial<Record<FeatureFlagKeyType, boolean>>

// Fetch the union of every known flag once. Callers slice from the cached
// result via `checkFeatureFlag(key)`. Previously the queryKey included the
// caller's specific key list, so two consumers asking for different but
// overlapping sets each fired their own /feature-flags/check-bulk request.
const ALL_FEATURE_FLAG_KEYS = Object.keys(
  FeatureFlagKey,
) as FeatureFlagKeyType[]
const ALL_API_KEYS = ALL_FEATURE_FLAG_KEYS.map(k => FeatureFlagKey[k].key)

/**
 * Hook to check feature flags for the current organization. All callers share
 * a single bulk fetch (one network request per org per stale window) regardless
 * of which subset of flags they care about.
 */
export function useFeatureFlags(_featureKeys: FeatureFlagKeyType[] = []) {
  const { currentOrganization } = useOrganizationStore()
  const organizationId = currentOrganization?.id ?? ''

  const flagsQuery = useQuery({
    queryKey: queryKeys.featureFlags.check(organizationId, '__all__'),
    queryFn: async () => {
      const results = await featureFlagsApi.checkBulk(ALL_API_KEYS)
      return ALL_FEATURE_FLAG_KEYS.reduce<FeatureFlagCheckMap>((acc, key) => {
        const apiKey = FeatureFlagKey[key].key
        acc[key] = results[apiKey] ?? false
        return acc
      }, {})
    },
    enabled: !!organizationId,
    staleTime: 30 * 1000,
  })

  const flagsData = flagsQuery.data

  const checkFeatureFlag = useCallback(
    (featureKey?: FeatureFlagKeyType): boolean => {
      if (!featureKey) return true
      return flagsData?.[featureKey] ?? false
    },
    [flagsData],
  )

  return {
    // `isLoading` is false while TanStack Query is paused by cache restore
    // (`status: pending`, `fetchStatus: idle`). Route guards must still wait in
    // that state; otherwise missing data is interpreted as a disabled feature
    // and a hard refresh can redirect away from the requested route.
    isLoading: flagsQuery.isPending,
    // A failed fetch with nothing cached is unknown, not "disabled"; guards
    // offer a retry instead of telling people the module is off.
    isError: flagsQuery.isError && !flagsData,
    refetch: flagsQuery.refetch,
    checkFeatureFlag,
    flagsData,
  }
}
